import React, { useRef, useCallback } from "react";
import { useAnimation } from "./AnimationContext";
import { Trash2, MousePointer } from "lucide-react";

export default function SlideCanvas({ slideContent = null, slideImageUrl }) {
  const {
    elements,
    selectedElementId,
    setSelectedElementId,
    updateElementState,
    addNewElementFromAsset,
    deleteElement,
    canvasBackground,
    canvasBackgroundColor,
  } = useAnimation();

  const opRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // ── Mouse-based element drag ──────────────────────────────────────────────
  const handleElementMouseDown = useCallback(
    (e, id) => {
      e.stopPropagation();
      e.preventDefault();
      const el = elements.find((el) => el.id === id);
      if (!el) return;
      setSelectedElementId(id);
      opRef.current = {
        type: "drag",
        id,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startElX: el.state.position.x,
        startElY: el.state.position.y,
      };
    },
    [elements, setSelectedElementId],
  );

  // ── Mouse-based resize from corner handles ────────────────────────────────
  const handleResizeMouseDown = useCallback(
    (e, id, corner) => {
      e.stopPropagation();
      e.preventDefault();
      const el = elements.find((el) => el.id === id);
      if (!el) return;
      opRef.current = {
        type: "resize",
        id,
        corner,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startElX: el.state.position.x,
        startElY: el.state.position.y,
        startW: el.state.size.w,
        startH: el.state.size.h,
      };
    },
    [elements],
  );

  const handleMouseMove = useCallback(
    (e) => {
      const op = opRef.current;
      if (!op) return;
      if (!canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const canvasW = rect.width;
      const canvasH = rect.height;

      const dxPixels = e.clientX - op.startMouseX;
      const dyPixels = e.clientY - op.startMouseY;

      const dx = (dxPixels / canvasW) * 100;
      const dy = (dyPixels / canvasH) * 100;

      if (op.type === "drag") {
        updateElementState(op.id, {
          position: {
            x: Math.round((op.startElX + dx) * 100) / 100,
            y: Math.round((op.startElY + dy) * 100) / 100,
          },
        });
      } else if (op.type === "resize") {
        let newX = op.startElX;
        let newY = op.startElY;
        let newW = op.startW;
        let newH = op.startH;

        if (op.corner === "br") {
          newW = Math.max(5, op.startW + dx);
          newH = Math.max(5, op.startH + dy);
        } else if (op.corner === "bl") {
          newW = Math.max(5, op.startW - dx);
          newH = Math.max(5, op.startH + dy);
          newX = op.startElX + (op.startW - newW);
        } else if (op.corner === "tr") {
          newW = Math.max(5, op.startW + dx);
          newH = Math.max(5, op.startH - dy);
          newY = op.startElY + (op.startH - newH);
        } else if (op.corner === "tl") {
          newW = Math.max(5, op.startW - dx);
          newH = Math.max(5, op.startH - dy);
          newX = op.startElX + (op.startW - newW);
          newY = op.startElY + (op.startH - newH);
        }

        updateElementState(op.id, {
          position: {
            x: Math.round(newX * 100) / 100,
            y: Math.round(newY * 100) / 100,
          },
          size: {
            w: Math.round(newW * 100) / 100,
            h: Math.round(newH * 100) / 100,
          },
        });
      }
    },
    [updateElementState],
  );

  const handleMouseUp = useCallback(() => {
    opRef.current = null;
  }, []);

  // ── Asset sidebar drop onto canvas ────────────────────────────────────────
  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    try {
      const dataStr = e.dataTransfer.getData("text/plain");
      if (!dataStr) return;
      const asset = JSON.parse(dataStr);
      const rect = canvasRef.current.getBoundingClientRect();
      const xPixels = e.clientX - rect.left;
      const yPixels = e.clientY - rect.top;

      const x = Math.round((xPixels / rect.width) * 100);
      const y = Math.round((yPixels / rect.height) * 100);

      addNewElementFromAsset(asset, x, y);
    } catch (err) {
      console.error("Drop asset error:", err);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[32rem] lg:h-full bg-red-300 flex items-center justify-center overflow-hidden rounded-xl shadow-inner border border-slate-700/30 select-none"

    >
      <div
        ref={canvasRef}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={() => setSelectedElementId(null)}
        className="relative bg-white shrink-0 w-full h-full aspect-video"
        style={{
          containerType: "inline-size",
          backgroundImage: slideImageUrl
            ? `url("${slideImageUrl}")`
            : canvasBackground
              ? `url("${canvasBackground}")`
              : "none",
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundColor: canvasBackgroundColor,
        }}
      >
        <div className="w-full h-full relative">
          {/* Empty state */}
          {elements.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
              <MousePointer className="w-10 h-10 text-white/20 mb-3" />
              <p className="text-white/40 text-sm font-medium">
                Canvas is empty
              </p>
              <p className="text-white/25 text-xs mt-1">
                Upload an image asset or click "Add Text Element" from the
                sidebar
              </p>
            </div>
          )}

          {elements.map((el) => {
            const isSelected = selectedElementId === el.id;
            return (
              <div
                key={el.id}
                id={`canvas-el-${el.id}`}
                onMouseDown={(e) => handleElementMouseDown(e, el.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedElementId(el.id);
                }}
                className={`absolute rounded cursor-move ${
                  isSelected
                    ? "ring-2 ring-purpleColor shadow-lg ring-offset-2 ring-offset-slate-900"
                    : ""
                }`}
                style={{
                  left: `${el.state.position.x}%`,
                  top: `${el.state.position.y}%`,
                  width: `${el.state.size.w}%`,
                  height: `${el.state.size.h}%`,
                  transform: `rotate(${el.state.rotation || 0}deg) scale(${el.state.scale || 1})`,
                  opacity: el.state.opacity ?? 1,
                  display: "flex",
                  // Text grows taller than its box at large font sizes. Centering would
                  // balloon that overflow symmetrically above and below (often off-canvas);
                  // anchoring to the top keeps growth predictable from the element's position.
                  alignItems: el.type === "text" ? "flex-start" : "center",
                  justifyContent: "center",
                }}
              >
                {el.type === "image" ? (
                  el.src ? (
                    <img
                      src={el.src}
                      alt={el.name}
                      draggable={false}
                      className="w-full h-full object-contain rounded pointer-events-none select-none"
                    />
                  ) : (
                    <div className="w-full h-full border border-dashed border-purpleColor/60 bg-purpleColor/10 rounded flex items-center justify-center">
                      <span className="text-purpleColor/70 text-xs font-semibold uppercase tracking-wide px-2 text-center">
                        {el.name}
                      </span>
                    </div>
                  )
                ) : el.type === "button" ? (
                  <button className="w-full h-full bg-purpleColor hover:bg-[#7a1b51] text-white font-bold text-sm rounded-lg shadow-md flex items-center justify-center px-4 pointer-events-none">
                    {el.text}
                  </button>
                ) : (
                  <div className="w-full h-full flex items-start px-2">
                    <h2
                      className="font-extrabold tracking-tight pointer-events-none leading-tight"
                      style={{
                        color: el.color || "#ffffff",
                        fontSize: `${(Number(el.fontSize) || 28) / 10}cqi`,
                      }}
                    >
                      {el.text}
                    </h2>
                  </div>
                )}

                {isSelected && (
                  <>
                    <button
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        deleteElement(el.id);
                      }}
                      className="absolute -top-3 -right-3 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg z-50 transition"
                      title="Delete element"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>

                    {/* Functional resize corners */}
                    <div
                      onMouseDown={(e) => handleResizeMouseDown(e, el.id, "tl")}
                      className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-purpleColor rounded-full cursor-nwse-resize z-40"
                    />
                    <div
                      onMouseDown={(e) => handleResizeMouseDown(e, el.id, "tr")}
                      className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-purpleColor rounded-full cursor-nesw-resize z-40"
                    />
                    <div
                      onMouseDown={(e) => handleResizeMouseDown(e, el.id, "bl")}
                      className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-purpleColor rounded-full cursor-nesw-resize z-40"
                    />
                    <div
                      onMouseDown={(e) => handleResizeMouseDown(e, el.id, "br")}
                      className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-purpleColor rounded-full cursor-nwse-resize z-40"
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-4 right-4 bg-slate-800/90 text-white text-[11px] font-mono px-3 py-1.5 rounded-lg border border-slate-700/50 shadow flex items-center gap-1.5 pointer-events-none">
        <span className="w-2 h-2 rounded-full bg-purpleColor animate-pulse" />
        <span>Canvas View Mode: Drag & Drop Enabled</span>
      </div>
    </div>
  );
}
